// Always-visible MMT-style "Modify Search" bar for the Flights results page.
// Mirrors the layout of HotelModifySearch — no expand/collapse, fields stay editable.
import React, { useEffect, useState } from 'react';
import { ArrowLeftRight, Search } from 'lucide-react';
import { toast } from 'sonner';

const todayIso = () => new Date().toISOString().slice(0, 10);

const Field = ({ label, children }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-red-400 transition-colors px-3 py-1 overflow-visible">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">{label}</p>
    {children}
  </div>
);

const FlightModifySearch = ({ values = {}, onSearch }) => {
  const [draft, setDraft] = useState(() => ({
    origin: (values.origin || 'DEL').toUpperCase(),
    destination: (values.destination || 'BOM').toUpperCase(),
    departure_date: values.departure_date || todayIso(),
    adults: parseInt(values.adults, 10) || 1,
    cabin_class: values.cabin_class || 'Economy',
  }));

  // Keep draft in sync if the URL-driven values change (e.g., browser back/forward).
  useEffect(() => {
    setDraft((d) => ({
      ...d,
      origin: (values.origin || d.origin || 'DEL').toUpperCase(),
      destination: (values.destination || d.destination || 'BOM').toUpperCase(),
      departure_date: values.departure_date || d.departure_date,
      adults: parseInt(values.adults, 10) || d.adults || 1,
      cabin_class: values.cabin_class || d.cabin_class || 'Economy',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.origin, values.destination, values.departure_date, values.adults, values.cabin_class]);

  const swap = () => setDraft((d) => ({ ...d, origin: d.destination, destination: d.origin }));

  const submit = () => {
    if (!draft.origin || !draft.destination) {
      toast.error('Please enter both origin and destination airport codes');
      return;
    }
    if (draft.origin === draft.destination) {
      toast.error('Origin and destination cannot be the same');
      return;
    }
    onSearch?.(draft);
  };

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr_1fr_1.1fr_auto] items-stretch gap-2">
          {/* From */}
          <div className="relative">
            <Field label="From">
              <input
                data-testid="flight-modify-origin"
                value={draft.origin}
                maxLength={3}
                onChange={(e) => setDraft({ ...draft, origin: e.target.value.toUpperCase() })}
                className="w-full bg-transparent outline-none text-sm font-bold text-gray-900 dark:text-white leading-tight uppercase tracking-wide"
              />
            </Field>
            {/* Swap button — centered between From and To using -right offset that matches gap */}
            <button
              type="button"
              onClick={swap}
              aria-label="Swap origin and destination"
              data-testid="flight-modify-swap-btn"
              className="hidden md:grid absolute -right-5 top-1/2 -translate-y-1/2 z-10 h-8 w-8 place-items-center rounded-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 shadow-md hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:scale-110 active:scale-95 transition-all"
            >
              <ArrowLeftRight className="h-4 w-4" />
            </button>
          </div>

          {/* To */}
          <Field label="To">
            <input
              data-testid="flight-modify-destination"
              value={draft.destination}
              maxLength={3}
              onChange={(e) => setDraft({ ...draft, destination: e.target.value.toUpperCase() })}
              className="w-full bg-transparent outline-none text-sm font-bold text-gray-900 dark:text-white leading-tight uppercase tracking-wide"
            />
          </Field>

          {/* Departure date */}
          <Field label="Departure">
            <input
              data-testid="flight-modify-date"
              type="date"
              value={draft.departure_date}
              min={todayIso()}
              onChange={(e) => setDraft({ ...draft, departure_date: e.target.value })}
              className="w-full bg-transparent outline-none text-sm font-bold text-gray-900 dark:text-white leading-tight"
            />
          </Field>

          {/* Travellers */}
          <Field label="Travellers">
            <input
              data-testid="flight-modify-adults"
              type="number"
              min={1}
              max={9}
              value={draft.adults}
              onChange={(e) => setDraft({ ...draft, adults: parseInt(e.target.value, 10) || 1 })}
              className="w-full bg-transparent outline-none text-sm font-bold text-gray-900 dark:text-white leading-tight"
            />
          </Field>

          {/* Class */}
          <Field label="Class">
            <select
              data-testid="flight-modify-class"
              value={draft.cabin_class}
              onChange={(e) => setDraft({ ...draft, cabin_class: e.target.value })}
              className="w-full bg-transparent outline-none text-sm font-bold text-gray-900 dark:text-white leading-tight"
            >
              <option value="Economy">Economy</option>
              <option value="Premium Economy">Premium Economy</option>
              <option value="Business">Business</option>
              <option value="First">First</option>
            </select>
          </Field>

          {/* Search button */}
          <button
            data-testid="flight-modify-search-btn"
            onClick={submit}
            className="bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold flex items-center justify-center gap-2 transition-all shadow-md rounded-xl tracking-wider text-sm px-5 py-2 md:py-0"
          >
            <Search className="h-4 w-4" /> SEARCH
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightModifySearch;
